<template>
  <CatNicknameDialogBase :on-click-outside="onClickOutside">
    <template #header>
      <div class="dialog-header">
        <div class="cat-display">
          <cat-icon size="large" :clickable="false" :writer-type="assistantCat?.assistantCatType" />
        </div>
        <h2 class="dialog-title">{{ $t("myPage.catNicknameDialog.title") }}</h2>
        <p class="dialog-subtitle">{{ dialogSubtitle }}</p>
      </div>
    </template>

    <template #body>
      <div class="dialog-body">
        <div class="input-wrapper">
          <input
            ref="nicknameInput"
            v-model="nickname"
            type="text"
            :placeholder="$t('myPage.catNicknameDialog.placeholder')"
            maxlength="30"
            class="nickname-input"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="dialog-footer">
        <button class="button cancel" @click="onCancel">{{ $t("common.cancel") }}</button>
        <div class="divider"></div>
        <button class="button primary" @click="onSave">{{ $t("common.save") }}</button>
      </div>
    </template>
  </CatNicknameDialogBase>
</template>

<script lang="ts">
import Vue from "vue";
import CatIcon from "@/components/atoms/CatIcon.vue";
import CatNicknameDialogBase from "@/components/ui/CatNicknameDialogBase.vue";
import { AssistantCat } from "@/lib/models/assistantCat";

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    CatIcon,
    CatNicknameDialogBase,
  },

  props: {
    assistantCat: {
      type: Object as () => AssistantCat | null,
      default: null,
    },
    initialNickname: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      nickname: this.initialNickname,
    };
  },

  computed: {
    dialogSubtitle(): string {
      const catName = this.assistantCat?.officialName || "";
      return this.$t("myPage.catNicknameDialog.subtitle", { catName }) as string;
    },
  },

  watch: {
    initialNickname(newValue: string) {
      this.nickname = newValue;
    },
  },

  methods: {
    onCancel() {
      this.$emit("cancel");
    },

    onSave() {
      this.$emit("save", this.nickname.trim());
    },

    onClickOutside() {
      this.onCancel();
    },
  },

  mounted() {
    // フォーカスを当てる
    if (this.$refs.nicknameInput) {
      (this.$refs.nicknameInput as HTMLInputElement).focus();
    }
  },
});

interface Data {
  nickname: string;
}

interface Methods {
  onCancel: () => void;
  onSave: () => void;
  onClickOutside: () => void;
}

interface Computed {
  dialogSubtitle: string;
}

interface Props {
  assistantCat: AssistantCat | null;
  initialNickname: string;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.dialog-header {
  text-align: center;
  margin-bottom: 20px;

  .cat-display {
    margin-bottom: 15px;
    display: flex;
    justify-content: center;
  }

  .dialog-title {
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
    margin-bottom: 8px;
    color: var(--color-text-normal);
  }

  .dialog-subtitle {
    font-size: var(--font-size-sm);
    color: var(--color-text-low);
    margin: 0;
  }
}

.dialog-body {
  margin-bottom: 20px;

  .input-wrapper {
    width: 100%;
  }

  .nickname-input {
    width: 100%;
    padding: 10px 12px;
    border: 2px solid var(--color-border);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-sm);
    outline: none;
    transition: border-color 0.2s ease;
    box-sizing: border-box;
    background-color: var(--color-surface-subtle);
    color: var(--color-text-normal);

    &:focus {
      border-color: var(--color-primary);
    }

    &::placeholder {
      color: var(--color-text-low);
    }
  }
}

.dialog-footer {
  margin: 20px -20px 0 -20px;
  border-top: 1px solid var(--color-border);
  display: flex;
  border-bottom-left-radius: 15px;
  border-bottom-right-radius: 15px;
  overflow: hidden;

  .button {
    flex: 1;
    height: 36px;
    border: none;
    background-color: transparent;
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &.cancel {
      color: var(--color-text-low);
    }

    &.primary {
      color: var(--color-primary);

      &:disabled {
        color: var(--color-border);
        cursor: not-allowed;
      }
    }
  }

  .divider {
    width: 1px;
    align-self: stretch;
    background-color: var(--color-border);
    flex-shrink: 0;
  }
}

@media (max-width: 576px) {
  .dialog-footer {
    margin: 15px -15px 0 -15px;
  }
}
</style>
